import ChevronIcon from '@centered/components/dist/icons/ChevronIcon'
import Medal from '@centered/components/dist/icons/Medal'
import SpinnerIcon from '@centered/components/dist/icons/SpinnerIcon'
import { converter, db, FIREBASE_CLOUD_FUNCTIONS_URL } from '@centered/firebase'
import { Group } from '@centered/types'
import { Listbox } from '@headlessui/react'
import { DateTime } from 'luxon'
import Image from 'next/image'
import { useEffect, useState } from 'react'

export type LeaderboardRange = 'Today' | 'This Week' | 'This Month' | 'All Time'

interface MetricEntry {
  id: string
  completedTasks: number
  score?: number
}

type GroupWithScore = Group & {
  stats: {
    score: number
  }
}

export default function GroupsLeaderboard(): JSX.Element {
  const [selectedTopUserMenu, setSelectedTopUserMenu] =
    useState<LeaderboardRange>('This Week')
  const [loaded, setLoaded] = useState(false)

  const [userChangedSelection, setUserChangedSelection] = useState(false)

  const [groups, setGroups] = useState<GroupWithScore[]>([])
  useEffect(() => {
    setLoaded(false)

    const today = DateTime.now().setZone('America/Los_Angeles')
    const dailyDocId = today.toFormat('yyyy-LL-dd')
    const weeklyDocId = today.toFormat(`yyyy-'W'WW`)
    const monthlyDocId = today.toFormat('yyyy-LL')
    const id =
      selectedTopUserMenu === 'All Time'
        ? 'alltime'
        : selectedTopUserMenu === 'Today'
        ? dailyDocId
        : selectedTopUserMenu === 'This Week'
        ? weeklyDocId
        : monthlyDocId

    const unsubscribe = db
      .collection(`v3-metrics/${id}/groups`)
      .where('isPrivate', '==', false)
      .where('score', '>=', 0)
      .orderBy('score', 'desc')
      .withConverter(converter<MetricEntry>())
      .limit(11)
      .onSnapshot(
        async (snapshot) => {
          const ids = snapshot.docs
            .map((doc) => doc.id)
            .filter((id) => id !== 'sukha-tribe')
            .slice(0, 10)

          const stats = snapshot.docs.map((doc) => doc.data())

          if (ids.length > 0) {
            const groupsSnapshot = await db
              .collection('groups')
              .where('id', 'in', ids)
              .withConverter(converter<Group>())
              .get()
            const groups = groupsSnapshot.docs.map((g) => g.data())

            const processedGroups = groups.slice(0, 10).map((group) => {
              const stat = stats.find((s) => s.id === group.id)
              // merge in custom stats from the leaderboard

              return {
                ...group,
                stats: {
                  ...group.stats,
                  score: stat?.score ?? 0,
                },
              }
            })

            setGroups(processedGroups)
          }

          setLoaded(true)
        },
        (error) => {
          console.error('leaderboard subscription error: ', error)
        },
      )

    return () => {
      unsubscribe()
    }
  }, [selectedTopUserMenu])

  // Show alltime if the current selection doesn't have anything
  useEffect(() => {
    if (loaded && groups.length === 0 && !userChangedSelection) {
      setSelectedTopUserMenu('All Time')
    }
  }, [groups.length, loaded, userChangedSelection])

  return (
    <div>
      <header className="flex justify-between items-center font-semibold mb-4 text-sm">
        <div className="flex items-center justify-between md:justify-start flex-1 gap-2">
          <p>Top Communities</p>

          <div className="flex items-center flex-row-reverse md:flex-row gap-2">
            <Listbox
              value={selectedTopUserMenu}
              onChange={(newRange) => {
                setSelectedTopUserMenu(newRange)
                setUserChangedSelection(true)
              }}
            >
              <div className="relative text-xs">
                <Listbox.Button className="relative cursor-pointer rounded-lg py-2 pl-3 pr-10 text-left border border-white border-opacity-10">
                  <span className="block truncate">{selectedTopUserMenu}</span>

                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronIcon direction="down" />
                  </span>
                </Listbox.Button>

                <Listbox.Options className="absolute top-full mt-1 max-h-60 w-auto overflow-auto rounded-md bg-white text-black py-1 shadow-lg">
                  {['Today', 'This Week', 'This Month', 'All Time'].map(
                    (timeRange, idx) => (
                      <Listbox.Option
                        key={idx}
                        className={() =>
                          `relative cursor-pointer select-none py-2 px-3`
                        }
                        value={timeRange}
                      >
                        {({ selected }) => (
                          <div
                            className={`truncate ${
                              selected ? 'font-semibold' : 'font-normal'
                            }`}
                          >
                            {timeRange}
                          </div>
                        )}
                      </Listbox.Option>
                    ),
                  )}
                </Listbox.Options>
              </div>
            </Listbox>

            {!loaded && (
              <div className="order-1">
                <SpinnerIcon />
              </div>
            )}
          </div>
        </div>

        <p className="hidden md:block">Productivity Score</p>
      </header>

      <div className="space-y-2">
        {groups
          .sort((a, z) => (z.stats.score > a.stats.score ? 1 : -1))
          .map((group, i) => (
            <div key={group.id} className="flex items-center gap-2 md:gap-4">
              <Image
                src={
                  group.featureUrl ??
                  group.coverUrl ??
                  `${FIREBASE_CLOUD_FUNCTIONS_URL}/randomPhoto?collectionId=j1fwrKAGDIg`
                }
                width={45}
                height={65}
                alt={group.name}
                className="rounded-md object-cover"
              />

              <div className="w-5 text-sm font-semibold flex-shrink-0 flex justify-center">
                {i === 0 ? (
                  <Medal rank={1} className="text-2xl flex-shrink-0 mt-1" />
                ) : i === 1 ? (
                  <Medal rank={2} className="text-2xl flex-shrink-0 mt-1" />
                ) : i === 2 ? (
                  <Medal rank={3} className="text-2xl flex-shrink-0 mt-1" />
                ) : (
                  `${i + 1}`
                )}
              </div>

              <div className="flex-1">
                <h1 className="text-sm md:text-lg">
                  {group.coach?.name ?? 'Noah'}
                </h1>

                <p className="text-white text-opacity-60 text-xs md:text-base">
                  {group.name} –{' '}
                  <span className="hidden md:inline">
                    {group.memberCount} members
                  </span>
                  <span className="md:hidden">{group.stats.score} pts</span>
                </p>
              </div>

              <div className="text-white text-opacity-60 hidden md:block">
                {group.stats?.score > 0 ? `${group.stats.score} pts` : '–'}
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}
